/**
 * VuMediChatbot is an AI chatbot intended to provide answers and insights to doctors.
 *
 * @see [STEAM-314](https://vumedi.atlassian.net/browse/STEAM-314)
 *
 * [USAGE]

```scss
// Usage code inside a view style file where AI chatbot should be displayed.
@use "~Styles/components/chatbot";

.vm-chatbot {
    // Rules to define parent component for both trigger and suggestions
}

.vm-chatbot-trigger {
    // Rules to define placement and size of the chatbot trigger component
}

.vm-chatbot-suggestions {
   // Rules to define placement and size of the chatbot suggestions
}
```

```javascript
// Usage code inside a view JS file where AI chatbot should be displayed.
import { VuMediChatbot } from 'Utils/vmChatbot';

const triggerContainer = document.querySelector('.vm-chatbot'); // Place for CTA and suggestions
const chatbot = new VuMediChatbot(browseSlug, channelSlug);     // It's enough to provide only one slug, browseSlug is preferred

// Initialize and render chatbot trigger element inside a specified container:
//
// * Will remove `hidden` class from the container.
// * Won't render if user is not allowed to use the chatbot.
// * Predefined questions will be automatically fetched and rendered.
await chatbot.initialize(triggerContainer);
```
 */
import * as Sentry from '@sentry/browser';
import lottie from 'lottie-web';
import { parse } from 'marked';

import { buildURL, fetchRequest } from 'Utils/apiFetchRequest';
import { ensureThreeLines } from 'Utils/text';
import { UserStore } from 'Main/UserStore';

const Copy = {
    placeholder: {
        default: 'Ask Vumedi AI...',
        loading: 'Searching...',
    },
    tooltip: 'Get all your questions answered with your new Vumedi **AI** Chat',
    trigger: 'Ask Vumedi AI',
    error: {
        questionResponse: 'Sorry, I could not compute.',
    },
};

export class VuMediChatbot {
    constructor(browseSlug = '', channelSlug = '') {
        this.browseSlug = browseSlug;
        this.channelSlug = channelSlug;

        this.tooltipElement = null;
        this.triggerElement = null;
        this.suggestionsElement = null;
        this.modalElement = null;
        this.uuid = null;
        this.inProgress = false;
        this.skeletonMessageInterval = null;
    }

    get slugParameter() {
        if (this.browseSlug) {
            return { browse: this.browseSlug };
        }

        if (this.channelSlug) {
            return { channel: this.channelSlug };
        }

        return {};
    }

    _askQuestion(question, type = '') {
        if (!this.modalElement) {
            return;
        }

        this.inProgress = true;

        this.modalElement.querySelector('.vm-chatbot-body').classList.add('vm-chatbot-body--conversation');
        this.modalElement.querySelector('input[type="text"]').value = '';
        this.modalElement.querySelector('input[type="text"]').setAttribute('placeholder', Copy.placeholder.loading);
        this.modalElement.querySelector('button[type="submit"]').setAttribute('disabled', 'disabled');

        this._renderQuestion(question);

        const options = {
            method: 'POST',
            body: JSON.stringify({ question, question_source: type }),
        };

        this._toggleSkeleton(true);

        fetchRequest(`/api/chats/${this.uuid}/ask/`, options)
            .then((data) => {
                if (typeof data?.answer === 'string' && data.answer.length) {
                    this._renderAnswer(data.answer, data?.next_best_questions);
                    return;
                }

                this._renderAnswer(Copy.error.questionResponse);
            })
            .catch((error) => {
                this._renderAnswer(Copy.error.questionResponse);
                Sentry.captureException(error);
            })
            .finally(() => {
                this._toggleSkeleton(false);
                this.inProgress = false;

                if (this.modalElement?.hasAttribute('open')) {
                    this.modalElement.querySelector('button[type="submit"]').removeAttribute('disabled');
                    this.modalElement
                        .querySelector('input[type="text"]')
                        .setAttribute('placeholder', Copy.placeholder.default);
                }
            });
    }

    _handleCloseClick() {
        if (!this.modalElement) {
            return;
        }

        this.modalElement.close();
        this.modalElement.querySelector('.vm-chatbot-body').classList.remove('vm-chatbot-body--conversation');
        document.body.style.overflow = '';

        const messages = this.modalElement.querySelectorAll('.vm-chatbot-messages .vm-chatbot-message');

        if (!messages?.length) {
            return;
        }

        messages.forEach((el) => el.remove());
    }

    _handleTooltipCloseClick() {
        this.tooltipElement?.classList.add('hidden');
        UserStore.saveAITooltipStatus(true);
    }

    _handleTooltipVisibility() {
        if (!UserStore.loadAITooltipStatus()) {
            this.tooltipElement?.classList.remove('hidden');
        }
    }

    _handleTriggerClick(question = '', skipLoading = false) {
        if (!this.modalElement) {
            return;
        }

        if (question && skipLoading) {
            this.modalElement.querySelector('input[type="text"]').value = question;
            this.modalElement.querySelector('input[type="text"]').setAttribute('data-type', 'predefined');
            this.modalElement.querySelector('button[type="submit"]').click();
            return;
        }

        this.modalElement.querySelector('.vm-chatbot-body').classList.add('is-loading');
        this._handleTooltipCloseClick();

        if (!this.modalElement.hasAttribute('open')) {
            this.modalElement.setAttribute('data-state', 'ready');
            this.modalElement.showModal();
            document.body.style.overflow = 'hidden';
        }

        const options = {
            method: 'POST',
            body: JSON.stringify(this.slugParameter),
        };

        fetchRequest('/api/chats/', options)
            .then((data) => {
                if (!data?.uuid) {
                    Sentry.captureException(new Error('Invalid response from the server'));
                    return;
                }

                this.modalElement.querySelector('.vm-chatbot-body').classList.remove('is-loading');
                this.uuid = data.uuid;
                this.inProgress = false;
                this.modalElement.querySelector('button[type="submit"]').removeAttribute('disabled');
                this.modalElement.querySelector('input[type="text"]').focus();
                this.modalElement
                    .querySelector('input[type="text"]')
                    .setAttribute('placeholder', Copy.placeholder.default);

                if (question && this.modalElement) {
                    this.modalElement.querySelector('input[type="text"]').value = question;
                    this.modalElement.querySelector('input[type="text"]').setAttribute('data-type', 'predefined');
                    this.modalElement.querySelector('button[type="submit"]').click();
                } else {
                    this.modalElement.querySelector('input[type="text"]').setAttribute('data-type', 'user_question');
                }
            })
            .catch((error) => {
                this.modalElement?.close();
                this.modalElement?.removeAttribute('data-state');
                document.body.style.overflow = '';
                Sentry.captureException(error);
            });
    }

    _renderModalElement() {
        const markup = `<dialog class="vm-chatbot-container">
            <div class="vm-chatbot-modal">
                <div class="vm-chatbot-header">
                    ${this._vumediAiLogo()}
                    <span class="vm-chatbot-header__floating-title">${this._modalTitle()}</span>
                    <button type="button" class="vm-button vm-button--sm vm-chatbot-close">
                        <span>Close</span>
                        <i class="icon icon-x"></i>
                    </button>
                </div>
                <div class="vm-chatbot-body js_chatbotBody">
                    <div class="vm-chatbot-body__loader js_chatbotLoader"></div>
                    <div class="vm-chatbot-placeholder">
                        <div class="vm-chatbot-placeholder__icon">
                            <i class="icon icon-chatbot"></i>
                        </div>
                        <h4 class="vm-chatbot-placeholder__title">Ask <span>AI</span> anything about ${this._modalTitle()}</h4>
                        <p class="vm-chatbot-placeholder__text">or anything else you are looking for.</p>
                        <form class="vm-chatbot-placeholder__form">
                            <i class="icon icon-chatbot"></i>
                            <input type="text"
                                name="question"
                                class="vm-chatbot-input"
                                autocomplete="off"
                                placeholder="${Copy.placeholder.default}"
                                aria-label="Question for AI" />
                            <button type="submit" class="vm-button vm-button--sm vm-button--primary vm-chatbot-send js_chatbotSend">
                                <i class="icon icon-arrow-up"></i>
                            </button>
                        </form>
                        <div class="vm-chatbot-placeholder__suggestions js_chatbotPlaceholderSuggestions">
                            <p class="vm-chatbot-placeholder__suggestions-title">Popular Questions:</p>
                            ${this._copySuggestions()}
                        </div>
                    </div>
                    <div class="vm-chatbot-messages">
                        <div class="vm-chatbot-skeleton js_vmChatbotSkeleton">
                            <div class="vm-chatbot-skeleton__teaser-message-wrap">
                                <div class="vm-chatbot-skeleton__teaser-message-avatar js_chatbotTeaserMessageAvatar"></div>
                                <div class="vm-chatbot-skeleton__teaser-message-text js_chatbotTeaserMessageText">
                                    <span>Understanding Vumedi videos...</span>
                                    <span style="display: none;">Searching most recent updates...</span>
                                    <span style="display: none;">Finding relevant videos...</span>
                                </div>
                            </div>
                            <div class="vm-chatbot-skeleton__lines-wrap">
                                ${this._skeletonLines()}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="vm-chatbot-footer">
                    <form class="vm-chatbot-footer__form">
                        <i class="icon icon-chatbot"></i>
                        <input type="text"
                               name="question"
                               class="vm-chatbot-input"
                               autocomplete="off"
                               placeholder="${Copy.placeholder.default}"
                               data-type="user_question"
                               aria-label="Question for AI" />
                        <button type="submit" class="vm-button vm-button--sm vm-button--primary vm-chatbot-send js_chatbotSend">
                            <i class="icon icon-arrow-up"></i>
                        </button>
                    </form>
                </div>
            </div>
        </dialog>`;

        const template = document.createElement('template');
        template.innerHTML = markup;

        const containerElement = template.content.firstChild;

        containerElement.addEventListener('close', () => this._handleCloseClick());
        containerElement.querySelector('.vm-chatbot-close').addEventListener('click', (ev) => {
            ev.preventDefault();
            this._handleCloseClick();
        });

        const forms = Array.from(containerElement.querySelectorAll('form'));

        forms.forEach((form) => {
            form.addEventListener('submit', (ev) => {
                ev.preventDefault();

                if (this.inProgress) {
                    return;
                }

                const inputElement = form.querySelector('input[type="text"]');

                const question = inputElement.value.trim();
                inputElement.value = '';

                if (!question) {
                    return;
                }

                this._askQuestion(question, inputElement.getAttribute('data-type') || '');
            });
        });

        return containerElement;
    }

    _renderQuestion(question) {
        if (!this.modalElement) {
            return;
        }

        const questionElement = document.createElement('div');
        const skeletonElement = this.modalElement.querySelector('.js_vmChatbotSkeleton');
        questionElement.classList.add('vm-chatbot-message', 'vm-chatbot-message--question');
        questionElement.innerText = question;

        const iconElement = document.createElement('i');
        iconElement.classList.add('icon', 'icon-chatbot');
        questionElement.prepend(iconElement);

        const messagesElement = this.modalElement.querySelector('.vm-chatbot-messages');
        messagesElement.insertBefore(questionElement, skeletonElement);

        setTimeout(() => {
            const bottom = skeletonElement.getBoundingClientRect().bottom + window.scrollY;
            const viewportHeight = window.innerHeight;

            messagesElement.scrollTo({
                top: bottom + viewportHeight,
                behavior: 'smooth',
            });
        }, 0);
    }

    _renderAnswer(answer, nextBestQuestions = []) {
        if (!this.modalElement) {
            return;
        }

        const answerElement = document.createElement('div');
        const skeletonElement = this.modalElement.querySelector('.js_vmChatbotSkeleton');
        answerElement.classList.add('vm-chatbot-message', 'vm-chatbot-message--answer');
        answerElement.innerHTML = parse(answer);
        answerElement.querySelectorAll('a').forEach((el) => el.setAttribute('target', '_blank'));

        const messagesElement = this.modalElement.querySelector('.vm-chatbot-messages');
        messagesElement.insertBefore(answerElement, skeletonElement);

        if (nextBestQuestions?.length) {
            const nextBestQuestionsElement = this._renderNextBestQuestions(nextBestQuestions);
            messagesElement.insertBefore(nextBestQuestionsElement, skeletonElement);
        }

        // Scroll so the last question is at the top of the viewport
        const lastQuestionElement = (() => {
            const questions = messagesElement.querySelectorAll('.vm-chatbot-message--question');

            if (!questions?.length) {
                return null;
            }

            return questions[questions.length - 1];
        })();

        if (!lastQuestionElement) {
            return;
        }

        const questionMargin = 24;

        messagesElement.scrollTo({
            top: Math.max(lastQuestionElement.offsetTop - lastQuestionElement.offsetHeight - questionMargin, 0),
            behavior: 'smooth',
        });
    }

    _renderNextBestQuestions(nextBestQuestions) {
        const nbqContainer = document.createElement('div');
        nbqContainer.classList.add('vm-chatbot-message', 'vm-chatbot-message--answer', 'vm-chatbot-message--nbq');

        const nbqHeading = document.createElement('h3');
        nbqHeading.innerText = 'Follow-up questions...';

        nbqContainer.appendChild(nbqHeading);

        nextBestQuestions.forEach((question) => {
            const questionElement = document.createElement('button');
            questionElement.setAttribute('type', 'button');
            questionElement.classList.add('vm-chatbot--nbq');

            const iconElement = document.createElement('i');
            iconElement.classList.add('icon', 'icon-chatbot');

            const textElement = document.createElement('span');
            textElement.innerText = question.question;

            questionElement.appendChild(iconElement);
            questionElement.appendChild(textElement);

            questionElement.addEventListener('click', (ev) => {
                ev.preventDefault();
                this._askQuestion(question.question, question.type);
            });

            nbqContainer.appendChild(questionElement);
        });

        return nbqContainer;
    }

    /**
     * Render a single suggested question.
     *
     * @param { id: number, question: string } question
     */
    _renderPredefined(question) {
        if (!this.suggestionsElement) {
            return;
        }

        const suggestionElement = document.createElement('button');
        suggestionElement.setAttribute('type', 'button');
        suggestionElement.setAttribute('id', question.id);
        suggestionElement.classList.add('vm-chatbot__suggestion');

        const contentElement = document.createElement('span');

        const iconElement = document.createElement('i');
        iconElement.classList.add('icon', 'icon-chatbot');

        contentElement.innerText = ensureThreeLines(question.question);
        contentElement.prepend(iconElement);

        suggestionElement.appendChild(contentElement);
        suggestionElement.addEventListener('click', (ev) => {
            ev.preventDefault();
            this._handleTriggerClick(question.question);
        });

        this.suggestionsElement.appendChild(suggestionElement);
    }

    _renderSuggestionsElement() {
        const containerElement = document.createElement('div');
        containerElement.classList.add('vm-chatbot__suggestions', 'vm-carousel-track', 'js_chatbotSuggestions');

        return containerElement;
    }

    _renderTooltipElement() {
        const tooltipContainer = document.createElement('div');
        tooltipContainer.classList.add('vm-chatbot__tooltip', 'hidden');

        const closeElement = document.createElement('button');
        closeElement.setAttribute('type', 'button');
        closeElement.classList.add('vm-chatbot__tooltip-close');

        const closeIconElement = document.createElement('i');
        closeIconElement.classList.add('icon', 'icon-x');

        closeElement.appendChild(closeIconElement);
        closeElement.addEventListener('click', (ev) => {
            ev.preventDefault();
            this._handleTooltipCloseClick();
        });

        const messageElement = document.createElement('div');
        messageElement.classList.add('vm-chatbot__tooltip-text');
        messageElement.innerHTML = parse(Copy.tooltip);

        const aiIconElement = document.createElement('i');
        aiIconElement.classList.add('icon', 'icon-chatbot');

        tooltipContainer.appendChild(closeElement);
        tooltipContainer.appendChild(messageElement);
        tooltipContainer.appendChild(aiIconElement);

        return tooltipContainer;
    }

    _renderTriggerElement() {
        const betaElement = document.createElement('span');
        betaElement.classList.add('vm-chatbot__trigger-badge');
        betaElement.innerText = 'Vumedi AI';

        const textElement = document.createElement('span');
        textElement.classList.add('vm-chatbot__trigger-text');
        textElement.innerText = Copy.trigger;

        const iconElement = document.createElement('i');
        iconElement.classList.add('icon', 'icon-chatbot');

        textElement.prepend(iconElement);

        const triggerElement = document.createElement('button');
        triggerElement.setAttribute('type', 'button');
        triggerElement.classList.add('vm-chatbot__trigger');

        triggerElement.appendChild(betaElement);
        triggerElement.appendChild(textElement);

        triggerElement.addEventListener('click', (ev) => {
            ev.preventDefault();
            this._handleTriggerClick();
        });

        return triggerElement;
    }

    _render(triggerContainer, predefined = []) {
        this.triggerElement = this._renderTriggerElement();
        this.suggestionsElement = this._renderSuggestionsElement();

        const carouselElement = document.createElement('div');
        carouselElement.classList.add('vm-carousel-container', 'js_vmCarousel');
        carouselElement.appendChild(this.suggestionsElement);

        const spacerElement = document.createElement('div');
        spacerElement.classList.add('vm-chatbot__spacer');

        this.tooltipElement = this._renderTooltipElement();

        // Place all units into DOM
        const contentElement = document.createElement('div');
        contentElement.classList.add('vm-chatbot__content');

        contentElement.appendChild(this.triggerElement);
        contentElement.appendChild(spacerElement);
        contentElement.appendChild(carouselElement);
        contentElement.appendChild(this.tooltipElement);

        triggerContainer.appendChild(contentElement);

        predefined.forEach((question) => this._renderPredefined(question));

        this.modalElement = this._renderModalElement();
        document.body.appendChild(this.modalElement);

        this._placeholderSuggestionsClickHandler();
        this._loadAnimation();
    }

    /**
     * Retrieve predefined questions for the provided browse slug.
     *
     * @returns Promise<{ status: 'error|forbidden|success', predefined: Array<{ question: string }> }>
     */
    _retrievePredefined() {
        return new Promise((resolve) => {
            const endpoint = buildURL('/api/chats/predefined-questions/', this.slugParameter);

            fetchRequest(endpoint)
                .then((data) => {
                    if (!Array.isArray(data?.results)) {
                        Sentry.captureException(new Error('Invalid response from the server'));
                        resolve({ status: 'error', predefined: [] });
                        return;
                    }

                    resolve({ status: 'success', predefined: data.results });
                })
                .catch((error) => {
                    if (error?.message === 'Forbidden') {
                        resolve({ status: 'forbidden', predefined: [] });
                        return;
                    }

                    Sentry.captureException(error);
                    resolve({ status: 'error', predefined: [] });
                });
        });
    }

    _copySuggestions() {
        const html = document
            .querySelector('.js_chatbotSuggestions')
            ?.innerHTML.replaceAll(
                'vm-chatbot__suggestion',
                'vm-chatbot-placeholder__suggestion js_chatbotPlaceholderSuggestion',
            )
            .replaceAll('<br>', '');

        return html;
    }

    _modalTitle() {
        const diseaseTitle = document.querySelector('.disease-column-page__title')?.innerText;
        const channelTitle = document.querySelector('.manufacturer_channel__title')?.innerText;

        return diseaseTitle || channelTitle || 'anything';
    }

    _placeholderSuggestionsClickHandler() {
        const placeholderSuggestions = Array.from(document.querySelectorAll('.js_chatbotPlaceholderSuggestion'));

        placeholderSuggestions.forEach((suggestionElement) => {
            suggestionElement.addEventListener('click', (ev) => {
                ev.preventDefault();
                this._handleTriggerClick(ev.target.innerText, true);
            });
        });
    }

    _loadAnimation() {
        const lottieAIAnimationPath = document.querySelector('meta[name="lottie-ai"]')?.content;
        const windowLoadAnimation = lottie.loadAnimation({
            container: document.querySelector('.js_chatbotLoader'),
            renderer: 'svg',
            loop: true,
            autoplay: true,
            path: lottieAIAnimationPath,
        });

        windowLoadAnimation.addEventListener('DOMLoaded', () => {
            windowLoadAnimation.setSpeed(0.66);
            windowLoadAnimation.playSegments([45, 135], true);
        });

        const answerLoadAnimation = lottie.loadAnimation({
            container: document.querySelector('.js_chatbotTeaserMessageAvatar'),
            renderer: 'svg',
            loop: true,
            autoplay: true,
            path: lottieAIAnimationPath,
        });

        answerLoadAnimation.addEventListener('DOMLoaded', () => {
            answerLoadAnimation.setSpeed(0.66);
            answerLoadAnimation.playSegments([45, 164], true);
        });
    }

    _vumediAiLogo() {
        const textualLogo = '<h3>Vumedi <strong>AI<i class="icon icon-chatbot-minimal"></i></strong></h3>';
        const logoSource = document.querySelector('meta[name="vumedi-ai-logo"]')?.content;
        const imgLogo = `<img class="vm-chatbot-header__logo" src="${logoSource}">`;

        return logoSource ? imgLogo : textualLogo;
    }

    _skeletonLines() {
        const lines = [];
        for (let i = 0; i < 16; i++) {
            const index = (i + 1) % 4 > 0 ? (i + 1) % 4 : 4;
            lines.push(`<div class="vm-chatbot-skeleton__line vm-chatbot-skeleton__line--${index}"></div>`);
        }
        return lines.join('');
    }

    _toggleSkeleton(show = true) {
        if (show) {
            const spans = document.querySelectorAll('.js_chatbotTeaserMessageText span');
            let current = 0;

            document.querySelector('.js_vmChatbotSkeleton').classList.add('is-visible');

            // Hide all spans except the first one
            spans.forEach((span, i) => {
                span.style.display = i === 0 ? 'inline' : 'none';
            });

            this.skeletonMessageInterval = setInterval(() => {
                // Hide current
                spans[current].style.display = 'none';

                // Move to next (loop around)
                current = (current + 1) % spans.length;

                // Show next
                spans[current].style.display = 'block';
            }, 3000);
        } else {
            document.querySelector('.js_vmChatbotSkeleton').classList.remove('is-visible');
            clearInterval(this.skeletonMessageInterval);
        }
    }

    async initialize(triggerContainer) {
        const { status, predefined } = await this._retrievePredefined();

        if (status === 'forbidden' || status === 'error') {
            return;
        }

        triggerContainer.classList.remove('hidden');

        this._render(triggerContainer, predefined);
        this._handleTooltipVisibility();
    }
}

/**
 * Extract browse term from the URL. Works on channel and disease pages.
 *
 * If browse term cannot be found, returns null.
 *
 * @returns {Object|null}
 */
export function extractSlug() {
    const pathName = window.location.pathname;
    const pathUnits = pathName.split('/').filter((x) => !!x);

    if (pathName.startsWith('/term/')) {
        return { browse: pathUnits.pop() };
    }

    if (pathName.startsWith('/channel/')) {
        const slug = pathUnits.pop();

        if (slug === 'browse') {
            return null;
        }

        return { channel: slug };
    }

    return null;
}
